import * as React from "react";
import { Input } from "@/components/ui/input";
import {
  CALLBACK_HORIZON_DAYS, callbackProblem, callbackWhen, defaultCallback, instantOf, isoWithOffset, knownZone,
  outsideCallingHours, ownZone, quickPicks, wallTime, zoneName, type WallTime,
} from "@/lib/callbacks";
import type { Disposition } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The day and the time of a callback (ADR-080), on the phone's outcome window and on Set outcome.
 *
 * Picked on the lead's clock, the one the dialer calls them on, with the rep's own beside it when
 * the two differ. It opens on the outcome's own wait — what the callback does given no time — so a
 * rep in a hurry confirms and goes; the quick picks are the times a rep says most. Outside the
 * lead's calling hours is a warning, not a refusal: the dialer waits for the hours to open.
 */

export interface CallbackTime {
  /** The clock the time is picked on. */
  zone: string;
  /** True: the lead's clock. False: nothing says where the lead is, so the rep's own. */
  leadClock: boolean;
  wall: WallTime;
  instant: number | null;
  problem: string | null;
  /** What core-api is sent, with the zone's offset; null while the time cannot be saved. */
  iso: string | null;
}

function described(zone: string, leadClock: boolean, wall: WallTime): CallbackTime {
  const instant = instantOf(wall, zone);
  const problem = callbackProblem(instant, Date.now());
  return {
    zone,
    leadClock,
    wall,
    instant,
    problem,
    iso: instant !== null && problem === null ? isoWithOffset(instant, zone) : null,
  };
}

/** The picker's state for a lead: `start` opens it on an outcome's own wait. The lead's zone can
 *  arrive after the picker opened (their card still loading): the time picked stays the same
 *  instant, said on the new clock. */
export function useCallbackTime(leadZone: string | null | undefined) {
  const lead = knownZone(leadZone);
  const zone = lead ?? ownZone();
  const [picked, setPicked] = React.useState<{ wall: WallTime; zone: string }>(() => ({
    wall: wallTime(Date.now(), zone),
    zone,
  }));
  const at = picked.zone === zone ? null : instantOf(picked.wall, picked.zone);
  const wall = at === null ? picked.wall : wallTime(at, zone);
  const set = React.useCallback((next: WallTime) => setPicked({ wall: next, zone }), [zone]);
  const start = React.useCallback(
    (outcome: Pick<Disposition, "backoff_seconds">) =>
      setPicked({ wall: wallTime(defaultCallback(outcome, Date.now()), zone), zone }),
    [zone],
  );
  return { value: described(zone, lead !== null, wall), set, start };
}

export function CallbackPicker({
  value, onChange, disabled,
}: {
  value: CallbackTime;
  onChange: (wall: WallTime) => void;
  disabled?: boolean;
}) {
  const id = React.useId();
  const now = Date.now();
  const today = wallTime(now, value.zone).date;
  const last = wallTime(now + CALLBACK_HORIZON_DAYS * 86_400_000, value.zone).date;
  const when = value.instant !== null ? callbackWhen(value.instant, value.leadClock ? value.zone : null) : null;
  const late = value.leadClock && value.instant !== null && value.problem === null && outsideCallingHours(value.instant, value.zone);

  return (
    <fieldset className="flex min-w-0 flex-col gap-1.5" disabled={disabled}>
      <legend className="mb-1.5 text-xs text-text-muted">
        <span className="font-medium text-text">Call back at</span>{" "}
        {value.leadClock ? `· their time (${zoneName(value.instant ?? now, value.zone)})` : "· your time: no time zone is known for them"}
      </legend>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,7.5rem)] gap-1.5">
        <Input
          id={`${id}-date`}
          type="date"
          aria-label="Callback date"
          min={today}
          max={last}
          required
          value={value.wall.date}
          invalid={value.problem !== null && value.wall.date !== ""}
          onChange={(e) => onChange({ ...value.wall, date: e.target.value })}
        />
        <Input
          id={`${id}-time`}
          type="time"
          aria-label="Callback time"
          step={300}
          required
          value={value.wall.time}
          invalid={value.problem !== null && value.wall.time !== ""}
          onChange={(e) => onChange({ ...value.wall, time: e.target.value })}
        />
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Quick picks">
        {quickPicks(now, value.zone).map((pick) => {
          const wall = wallTime(pick.instant, value.zone);
          const lit = wall.date === value.wall.date && wall.time === value.wall.time;
          return (
            <button
              key={pick.label}
              type="button"
              aria-pressed={lit}
              onClick={() => onChange(wall)}
              className={cn(
                "rounded-full border px-2 py-0.5 text-2xs transition-colors",
                lit
                  ? "border-accent-8 bg-accent-2 text-accent-11"
                  : "border-border bg-surface text-text-muted hover:bg-surface-hover",
              )}
            >
              {pick.label}
            </button>
          );
        })}
      </div>
      {value.problem ? (
        <p role="alert" className="text-2xs text-danger-11">
          {value.problem}
        </p>
      ) : (
        when && (
          <p className="text-2xs text-text-muted" aria-live="polite">
            {when.there}
            {when.yours && <> · {when.yours}</>}
          </p>
        )
      )}
      {late && (
        <p className="text-2xs text-warning-11">
          Outside 8 AM–9 PM for them: the dialer waits until their calling hours open.
        </p>
      )}
    </fieldset>
  );
}
